import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { avisar, confirmar, textoDeError } from '../components/Dialogo';

/**
 * App de pacientes → EN VIVO (0.59.0, functions/vivo.ts). Para un evento con mucha gente: se proyecta el QR (o se manda la
 * liga), cada quien lo abre en su teléfono, y lo que se manda aquí les llega a todos al mismo tiempo: en la página del
 * evento (pregunta cada ~1.5 s) y, a quien activó los avisos, como notificación (sale en paralelo: 100 teléfonos en ~1 s).
 * La página y el push los sirve Safevity Rewards; el QR también (un recuadro con `&solo=qr`), así aquí no hay otra librería.
 */
interface Evento { evento_id: string; codigo: string; nombre: string; descripcion: string; estado: 'abierto' | 'cerrado'; created_at: string; unidos: number; con_avisos: number; viendo: number; anuncios: number; liga: string; pantalla: string }
interface Anuncio { anuncio_id: string; titulo: string; cuerpo: string | null; boton_texto: string | null; boton_url: string | null; por_nombre: string | null; created_at: string; push_estado: string; push_resumen: any }

const inputCss: React.CSSProperties = { width: '100%', padding: '8px 10px', border: '1px solid var(--border)', borderRadius: 8, fontFamily: 'inherit', fontSize: 14, background: '#fff' };
const fechaHora = (x: string) => new Date(x).toLocaleString('es-MX', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });
const num = (n: number) => (n || 0).toLocaleString('es-MX');

function textoPush(a: Anuncio): string {
  const r = a.push_resumen;
  if (a.push_estado !== 'enviado' || !r) return a.push_estado === 'enviando' ? 'Mandando los avisos…' : 'Los avisos salen en menos de un minuto.';
  if (r.motivo && !r.telefonos) return `En la página para todos. Avisos: ${r.motivo}.`;
  if (r.error) return `En la página para todos. Los avisos no salieron: ${r.error}.`;
  if (!r.configurado) return 'En la página para todos. Los avisos no están configurados en la app.';
  return `En la página para todos · aviso a ${num(r.enviados)} de ${num(r.telefonos)} ${r.telefonos === 1 ? 'teléfono' : 'teléfonos'}${r.fallidos ? ` (${num(r.fallidos)} no lo recibieron)` : ''}${typeof r.ms === 'number' ? ` en ${(r.ms / 1000).toFixed(1)} s` : ''}.`;
}

export function EnVivo() {
  const [eventos, setEventos] = useState<Evento[] | null>(null);
  const [err, setErr] = useState('');
  const [sel, setSel] = useState<string | null>(() => new URLSearchParams(window.location.search).get('evento'));
  const [nuevo, setNuevo] = useState<{ nombre: string; descripcion: string } | null>(null);
  const [errNuevo, setErrNuevo] = useState('');

  const cargar = () => api<{ eventos: Evento[] }>('vivo').then((x) => { setEventos(x.eventos); setErr(''); }).catch((e) => setErr(textoDeError(e)));
  useEffect(() => { cargar(); }, []);
  const elegir = (id: string | null) => {
    setSel(id);
    const u = new URL(window.location.href); if (id) u.searchParams.set('evento', id); else u.searchParams.delete('evento');
    window.history.replaceState(null, '', u.toString());
  };

  async function crear(e: React.FormEvent) {
    e.preventDefault();
    if (!nuevo) return;
    if (!nuevo.nombre.trim()) { setErrNuevo('Ponle un nombre al evento.'); return; }
    try {
      const r = await api<{ evento: Evento }>('vivo', { method: 'POST', body: JSON.stringify(nuevo) });
      setNuevo(null); setErrNuevo(''); await cargar(); elegir(r.evento.evento_id);
    } catch (x) { setErrNuevo(textoDeError(x)); }
  }

  if (sel) return <DetalleEvento id={sel} onVolver={() => { elegir(null); cargar(); }} />;

  return (
    <div data-vivo-lista>
      <div className="card" style={{ padding: 16, marginBottom: 14 }}>
        <h2 style={{ margin: '0 0 6px', fontSize: 17, fontWeight: 600 }}>En vivo</h2>
        <p className="muted" style={{ margin: 0, fontSize: 13, lineHeight: 1.5 }}>
          Para un evento con mucha gente. Proyecta el QR (o manda la liga): cada quien lo abre en su teléfono, sin cuenta ni app.
          Lo que mandes aquí les llega a todos al mismo tiempo, en la página del evento en un par de segundos, y como notificación a quien la activó.
        </p>
        <div style={{ marginTop: 12 }}>
          {nuevo ? (
            <form onSubmit={crear} noValidate style={{ display: 'grid', gap: 8, maxWidth: 520 }} data-vivo-nuevo>
              <label style={{ fontSize: 13, fontWeight: 600 }}>Nombre del evento
                <input style={{ ...inputCss, marginTop: 4 }} value={nuevo.nombre} maxLength={80} autoFocus aria-invalid={!!errNuevo && !nuevo.nombre.trim()}
                  onChange={(e) => { setNuevo({ ...nuevo, nombre: e.target.value }); setErrNuevo(''); }} placeholder="Ej. Inauguración Safevity Polanco" data-vivo-nombre />
              </label>
              <label style={{ fontSize: 13, fontWeight: 600 }}>Descripción (opcional)
                <input style={{ ...inputCss, marginTop: 4 }} value={nuevo.descripcion} maxLength={300} onChange={(e) => setNuevo({ ...nuevo, descripcion: e.target.value })} placeholder="Sólo la ves tú" />
              </label>
              {errNuevo && <p role="alert" style={{ color: 'var(--danger, #b42318)', margin: 0, fontSize: 13 }}>{errNuevo}</p>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn-primary" type="submit" data-vivo-crear>Crear evento</button>
                <button className="btn-ghost" type="button" onClick={() => { setNuevo(null); setErrNuevo(''); }}>Cancelar</button>
              </div>
            </form>
          ) : <button className="btn-primary" onClick={() => setNuevo({ nombre: '', descripcion: '' })} data-vivo-nuevo-btn>+ Nuevo evento</button>}
        </div>
      </div>
      {err && <p role="alert" style={{ color: 'var(--danger, #b42318)' }}>{err}</p>}
      {!eventos && !err && <p className="muted">Cargando…</p>}
      {eventos && !eventos.length && <p className="muted" data-vivo-vacio>Todavía no hay eventos. Crea uno para tener su QR y mandar anuncios.</p>}
      <div style={{ display: 'grid', gap: 10, gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 300px), 1fr))' }}>
        {(eventos || []).map((ev) => (
          <button key={ev.evento_id} className="card" onClick={() => elegir(ev.evento_id)} data-vivo-evento={ev.codigo}
            style={{ textAlign: 'left', padding: 14, cursor: 'pointer', border: '1px solid var(--border)', font: 'inherit', color: 'inherit' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline' }}>
              <b style={{ fontSize: 15, overflowWrap: 'anywhere' }}>{ev.nombre}</b>
              <span style={{ fontSize: 11, fontWeight: 600, color: ev.estado === 'abierto' ? '#065f46' : 'var(--muted, #777)', whiteSpace: 'nowrap' }}>{ev.estado === 'abierto' ? '● Abierto' : 'Cerrado'}</span>
            </div>
            <div className="muted" style={{ fontSize: 12.5, marginTop: 6 }}>Código {ev.codigo} · {fechaHora(ev.created_at)}</div>
            <div style={{ fontSize: 13, marginTop: 6 }}>{num(ev.unidos)} se unieron · {num(ev.con_avisos)} con avisos · {num(ev.anuncios)} {ev.anuncios === 1 ? 'anuncio' : 'anuncios'}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function DetalleEvento({ id, onVolver }: { id: string; onVolver: () => void }) {
  const [d, setD] = useState<{ evento: Evento; anuncios: Anuncio[] } | null>(null);
  const [err, setErr] = useState('');
  const [titulo, setTitulo] = useState(''); const [cuerpo, setCuerpo] = useState('');
  const [conBoton, setConBoton] = useState(false); const [botonTexto, setBotonTexto] = useState(''); const [botonUrl, setBotonUrl] = useState('');
  const [errForm, setErrForm] = useState<{ campo?: string; texto: string } | null>(null);
  const [mandando, setMandando] = useState(false);
  const [copiado, setCopiado] = useState(false);
  const vivo = useRef(true);

  const cargar = () => api<{ evento: Evento; anuncios: Anuncio[] }>(`vivo/${id}`).then((x) => { if (vivo.current) { setD(x); setErr(''); } }).catch((e) => { if (vivo.current) setErr(textoDeError(e)); });
  useEffect(() => {
    vivo.current = true; cargar();
    // Los números (cuántos ven ahora) y el resultado de cada aviso se refrescan solos con la pantalla a la vista.
    const t = window.setInterval(() => { if (!document.hidden) cargar(); }, 5000);
    return () => { vivo.current = false; window.clearInterval(t); };
  }, [id]);

  if (err && !d) return <div><button className="btn-ghost" onClick={onVolver}>← Eventos</button><p role="alert" style={{ color: 'var(--danger, #b42318)' }}>{err}</p></div>;
  if (!d) return <p className="muted">Cargando el evento…</p>;
  const ev = d.evento; const abierto = ev.estado === 'abierto';

  async function mandar(e: React.FormEvent) {
    e.preventDefault();
    if (!titulo.trim()) { setErrForm({ campo: 'titulo', texto: 'Falta el título del anuncio.' }); return; }
    if (conBoton && !botonTexto.trim()) { setErrForm({ campo: 'boton_texto', texto: 'Falta lo que dice el botón.' }); return; }
    if (conBoton && !/^https:\/\/\S+$/i.test(botonUrl.trim())) { setErrForm({ campo: 'boton_url', texto: 'La liga del botón tiene que empezar con https://' }); return; }
    const ok = await confirmar({ titulo: 'Mandar el anuncio', confirmar: 'Mandar a todos',
      mensaje: `«${titulo.trim()}»\n\nAparece al momento en la página de quien la tenga abierta${ev.viendo ? ` (${num(ev.viendo)} ahora)` : ''} y llega como notificación a ${num(ev.con_avisos)} ${ev.con_avisos === 1 ? 'teléfono' : 'teléfonos'} con avisos.` });
    if (!ok) return;
    setMandando(true);
    try {
      const r = await api<{ anuncio: Anuncio; push_ahora: boolean }>(`vivo/${id}/anuncios`, { method: 'POST',
        body: JSON.stringify({ titulo, cuerpo, boton_texto: conBoton ? botonTexto : '', boton_url: conBoton ? botonUrl : '' }) });
      setTitulo(''); setCuerpo(''); setConBoton(false); setBotonTexto(''); setBotonUrl(''); setErrForm(null);
      await cargar();
      await avisar({ titulo: 'Anuncio enviado', tipo: 'ok', mensaje: r.push_ahora ? textoPush(r.anuncio) : 'Ya aparece en la página del evento. Los avisos salen en menos de un minuto.' });
    } catch (x: any) {
      const campo = x?.data?.campo || x?.body?.campo;
      setErrForm({ campo, texto: textoDeError(x) });
    } finally { setMandando(false); }
  }

  async function cambiarEstado(estado: 'abierto' | 'cerrado') {
    if (estado === 'cerrado' && !(await confirmar({ titulo: 'Cerrar el evento', confirmar: 'Cerrar evento', peligro: true,
      mensaje: 'La página dirá que el evento terminó y ya no se podrán mandar anuncios ni activar avisos. Puedes abrirlo otra vez después.' }))) return;
    try { await api(`vivo/${id}`, { method: 'PUT', body: JSON.stringify({ estado }) }); await cargar(); }
    catch (x) { await avisar({ titulo: 'No se pudo', tipo: 'error', mensaje: textoDeError(x) }); }
  }

  async function quitar(a: Anuncio) {
    if (!(await confirmar({ titulo: 'Quitar de la página', confirmar: 'Quitar', peligro: true,
      mensaje: `«${a.titulo}» deja de verse en la página del evento. La notificación que ya llegó a los teléfonos no se puede borrar.` }))) return;
    try { await api(`vivo/${id}/quitar`, { method: 'POST', body: JSON.stringify({ anuncio_id: a.anuncio_id }) }); await cargar(); }
    catch (x) { await avisar({ titulo: 'No se pudo', tipo: 'error', mensaje: textoDeError(x) }); }
  }

  async function copiar() {
    try { await navigator.clipboard.writeText(ev.liga); setCopiado(true); window.setTimeout(() => setCopiado(false), 2000); }
    catch { await avisar({ titulo: 'Copia la liga', mensaje: ev.liga }); }
  }

  const rojo = (campo: string) => errForm?.campo === campo;
  return (
    <div data-vivo-detalle={ev.codigo}>
      <button className="btn-ghost" onClick={onVolver} style={{ marginBottom: 10 }} data-vivo-volver>← Eventos</button>
      <div style={{ display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))', alignItems: 'start' }}>
        <div className="card" style={{ padding: 16, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600, overflowWrap: 'anywhere' }}>{ev.nombre}</h2>
            <span style={{ fontSize: 12, fontWeight: 600, color: abierto ? '#065f46' : 'var(--muted, #777)' }} data-vivo-estado>{abierto ? '● Abierto' : 'Cerrado'}</span>
          </div>
          {ev.descripcion && <p className="muted" style={{ margin: '6px 0 0', fontSize: 13 }}>{ev.descripcion}</p>}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8, margin: '14px 0' }} data-vivo-numeros>
            {[['Se unieron', ev.unidos], ['Con avisos', ev.con_avisos], ['Viendo ahora', ev.viendo]].map(([t, n]) => (
              <div key={t as string} style={{ background: 'var(--bg-alt, #faf7f0)', borderRadius: 10, padding: '8px 10px' }}>
                <div style={{ fontSize: 22, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{num(n as number)}</div>
                <div className="muted" style={{ fontSize: 11.5 }}>{t}</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
            <iframe title={`QR del evento ${ev.codigo}`} src={`${ev.liga}&solo=qr`} style={{ width: 168, height: 168, border: 0, borderRadius: 10, background: '#fff', flex: '0 0 auto' }} data-vivo-qr />
            <div style={{ minWidth: 0, flex: '1 1 240px' }}>
              <div className="muted" style={{ fontSize: 12 }}>Código</div>
              <div style={{ fontSize: 26, fontWeight: 700, letterSpacing: '0.2em' }} data-vivo-codigo>{ev.codigo}</div>
              <div style={{ fontSize: 12.5, overflowWrap: 'anywhere', margin: '6px 0 10px' }}>{ev.liga}</div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button className="btn-ghost" onClick={copiar} data-vivo-copiar>{copiado ? 'Copiada' : 'Copiar liga'}</button>
                <a className="btn-ghost" href={ev.pantalla} target="_blank" rel="noopener noreferrer" data-vivo-pantalla
                  style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', borderRadius: 25, padding: '13px 28px', fontWeight: 500, fontSize: 15 }}>Abrir pantalla para proyectar</a>
              </div>
            </div>
          </div>
          <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
            {abierto
              ? <button className="btn-peligro" onClick={() => cambiarEstado('cerrado')} data-vivo-cerrar>Cerrar evento</button>
              : <button className="btn-ghost" onClick={() => cambiarEstado('abierto')} data-vivo-abrir>Abrir otra vez</button>}
          </div>
        </div>

        <form className="card" style={{ padding: 16, minWidth: 0, display: 'grid', gap: 10 }} onSubmit={mandar} noValidate data-vivo-form>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>Mandar un anuncio</h3>
          {!abierto && <p className="muted" style={{ margin: 0, fontSize: 13 }}>El evento está cerrado: ábrelo otra vez para mandar anuncios.</p>}
          <label style={{ fontSize: 13, fontWeight: 600 }}>Título
            <input style={{ ...inputCss, marginTop: 4, ...(rojo('titulo') ? { borderColor: 'var(--danger, #b42318)' } : {}) }} value={titulo} maxLength={80} disabled={!abierto}
              onChange={(e) => { setTitulo(e.target.value); if (rojo('titulo')) setErrForm(null); }} placeholder="Ej. ¡Ya empieza la rifa!" aria-invalid={rojo('titulo')} data-vivo-titulo />
          </label>
          <label style={{ fontSize: 13, fontWeight: 600 }}>Texto (opcional)
            <textarea style={{ ...inputCss, marginTop: 4, minHeight: 80, resize: 'vertical' }} value={cuerpo} maxLength={400} disabled={!abierto}
              onChange={(e) => setCuerpo(e.target.value)} placeholder="Lo que quieres que lean todos" data-vivo-cuerpo />
          </label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
            <input type="checkbox" checked={conBoton} disabled={!abierto} onChange={(e) => setConBoton(e.target.checked)} data-vivo-con-boton /> Con un botón (una liga)
          </label>
          {conBoton && (
            <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 2fr)' }}>
              <input style={{ ...inputCss, ...(rojo('boton_texto') ? { borderColor: 'var(--danger, #b42318)' } : {}) }} value={botonTexto} maxLength={30} placeholder="Lo que dice" aria-label="Lo que dice el botón"
                onChange={(e) => { setBotonTexto(e.target.value); if (rojo('boton_texto')) setErrForm(null); }} aria-invalid={rojo('boton_texto')} data-vivo-boton-texto />
              <input style={{ ...inputCss, ...(rojo('boton_url') ? { borderColor: 'var(--danger, #b42318)' } : {}) }} value={botonUrl} maxLength={500} placeholder="https://…" aria-label="Liga del botón" inputMode="url"
                onChange={(e) => { setBotonUrl(e.target.value); if (rojo('boton_url')) setErrForm(null); }} aria-invalid={rojo('boton_url')} data-vivo-boton-url />
            </div>
          )}
          {errForm && <p role="alert" style={{ color: 'var(--danger, #b42318)', margin: 0, fontSize: 13 }} data-vivo-error>{errForm.texto}</p>}
          {(titulo.trim() || cuerpo.trim()) && (
            <div style={{ border: '1px solid var(--border)', borderRadius: 12, padding: 12, background: '#fff' }} data-vivo-vista>
              <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em' }}>Así se ve</div>
              <div style={{ fontSize: 17, fontWeight: 700, marginTop: 4, overflowWrap: 'anywhere' }}>{titulo || 'Título'}</div>
              {cuerpo && <div style={{ fontSize: 14, marginTop: 4, whiteSpace: 'pre-line', overflowWrap: 'anywhere' }}>{cuerpo}</div>}
              {conBoton && botonTexto && <div style={{ display: 'inline-block', marginTop: 8, padding: '6px 14px', borderRadius: 999, background: 'var(--gold, #AC996C)', color: '#fff', fontSize: 13, fontWeight: 600 }}>{botonTexto}</div>}
            </div>
          )}
          <div><button className="btn-primary" type="submit" disabled={!abierto || mandando} data-vivo-mandar>{mandando ? 'Mandando…' : 'Mandar a todos'}</button></div>
        </form>
      </div>

      <div className="card" style={{ padding: 16, marginTop: 14 }}>
        <h3 style={{ margin: '0 0 10px', fontSize: 16, fontWeight: 600 }}>Anuncios mandados ({num(d.anuncios.length)})</h3>
        {!d.anuncios.length && <p className="muted" style={{ margin: 0, fontSize: 13 }}>Todavía no mandas ninguno. El primero aparece en la página de todos en un par de segundos.</p>}
        <ol style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 10 }} data-vivo-historial>
          {d.anuncios.map((a) => (
            <li key={a.anuncio_id} style={{ borderTop: '1px solid var(--border)', paddingTop: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <b style={{ overflowWrap: 'anywhere' }}>{a.titulo}</b>
                <span className="muted" style={{ fontSize: 12 }}>{fechaHora(a.created_at)}{a.por_nombre ? ` · ${a.por_nombre}` : ''}</span>
              </div>
              {a.cuerpo && <div style={{ fontSize: 13.5, whiteSpace: 'pre-line', marginTop: 4, overflowWrap: 'anywhere' }}>{a.cuerpo}</div>}
              {a.boton_texto && <div className="muted" style={{ fontSize: 12.5, marginTop: 4, overflowWrap: 'anywhere' }}>Botón «{a.boton_texto}» → {a.boton_url}</div>}
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', marginTop: 6, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12.5 }} data-vivo-push>{textoPush(a)}</span>
                <button className="btn-ghost" style={{ padding: '3px 10px', fontSize: 12 }} onClick={() => quitar(a)} data-vivo-quitar>Quitar de la página</button>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
}
